import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  MenuItem,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  Stack,
  Divider,
  TableContainer,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getMovimientosPorFecha, getDetalleVenta, getGananciasPorFecha } from "../../../../services/modules/Reportes";
import { useAuth } from "../../../../context/AuthContext";

const currencyGTQ = new Intl.NumberFormat("es-GT", {
  style: "currency",
  currency: "GTQ",
  minimumFractionDigits: 2
});

const TIPO_LABEL = {
  contado: "Ventas al Contado",
  credito: "Ventas al Crédito (no suman al total)",
  abono: "Abonos",
  ingreso_reparacion: "Ingreso de Reparación"
};

const ReportePorFecha = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const { user } = useAuth();
  const isAdmin = String(user?.role || user?.rol || "").toLowerCase() === "admin";

  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [detalle, setDetalle] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [ventaSeleccionada, setVentaSeleccionada] = useState(null);
  const [tipo, setTipo] = useState("");
  const [movimientos, setMovimientos] = useState([]);
  const [mostrarGanancia, setMostrarGanancia] = useState(false);
  const [resumenGanancia, setResumenGanancia] = useState(null);

  useEffect(() => {
    if (!isAdmin && mostrarGanancia) {
      setMostrarGanancia(false);
      setResumenGanancia(null);
    }
  }, [isAdmin, mostrarGanancia]);

  const tiposAMostrar = tipo ? [tipo] : ["contado", "credito", "abono", "ingreso_reparacion"];

  const buscarMovimientos = async () => {
    if (!fechaInicio || !fechaFin) {
      alert("Selecciona ambas fechas");
      return;
    }
    const data = await getMovimientosPorFecha({ fechaInicio, fechaFin, tipo });
    setMovimientos(Array.isArray(data) ? data : []);

    if (mostrarGanancia) {
      const resumen = await getGananciasPorFecha({ fechaInicio, fechaFin, tipo });
      setResumenGanancia(resumen || null);
    } else {
      setResumenGanancia(null);
    }
  };

  const movimientosPorTipo = (tipoFiltro) =>
    (movimientos || []).filter(
      (m) => m?.tipo_movimiento?.toLowerCase?.() === tipoFiltro.toLowerCase()
    );

  const totalSinCredito = useMemo(
    () =>
      (movimientos || []).reduce((acc, m) => {
        const t = m?.tipo_movimiento?.toLowerCase?.();
        if (t !== "credito") {
          return acc + (parseFloat(m.total) || 0);
        }
        return acc;
      }, 0),
    [movimientos]
  );

  const verDetalle = async (idVenta) => {
    const d = await getDetalleVenta(idVenta);
    setDetalle(Array.isArray(d) ? d : []);
    setVentaSeleccionada(idVenta);
    setOpenModal(true);
  };

  return (
    <Box p={{ xs: 2, md: 3 }}>
      <Typography variant="h5" gutterBottom>
        Reporte de Movimientos por Fecha
      </Typography>

      {/* Filtros */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <TextField
          select
          label="Tipo de Movimiento"
          value={tipo}
          onChange={(e) => setTipo(e.target.value)}
          sx={{ minWidth: 220 }}
          size="small"
        >
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="contado">Ventas al Contado</MenuItem>
          <MenuItem value="credito">Ventas al Crédito</MenuItem>
          <MenuItem value="abono">Abonos</MenuItem>
          <MenuItem value="ingreso_reparacion">Pagos de Reparaciones</MenuItem>
        </TextField>

        <TextField
          label="Fecha Inicio"
          type="date"
          value={fechaInicio}
          onChange={(e) => setFechaInicio(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="Fecha Fin"
          type="date"
          value={fechaFin}
          onChange={(e) => setFechaFin(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={mostrarGanancia}
              disabled={!isAdmin}
              onChange={async (e) => {
                const checked = e.target.checked;
                setMostrarGanancia(checked);
                if (!checked) {
                  setResumenGanancia(null);
                  return;
                }
                if (fechaInicio && fechaFin) {
                  const resumen = await getGananciasPorFecha({ fechaInicio, fechaFin, tipo });
                  setResumenGanancia(resumen || null);
                }
              }}
            />
          }
          label="Mostrar ganancia"
        />
        <Button variant="contained" onClick={buscarMovimientos}>
          Buscar
        </Button>
      </Box>

      {/* Resumen de ganancia */}
      {mostrarGanancia && resumenGanancia && (
        <Box mb={3}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} divider={<Divider flexItem />}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Productos
                </Typography>
                <Typography>
                  Venta:{" "}
                  <strong>{currencyGTQ.format(parseFloat(resumenGanancia?.totales_productos?.total_venta) || 0)}</strong>
                </Typography>
                <Typography>
                  Costo:{" "}
                  <strong>{currencyGTQ.format(parseFloat(resumenGanancia?.totales_productos?.total_costo) || 0)}</strong>
                </Typography>
                <Typography>
                  Ganancia:{" "}
                  <strong>{currencyGTQ.format(parseFloat(resumenGanancia?.totales_productos?.ganancia) || 0)}</strong>
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Servicios
                </Typography>
                <Typography>
                  Venta:{" "}
                  <strong>{currencyGTQ.format(parseFloat(resumenGanancia?.totales_servicios?.total_venta) || 0)}</strong>
                </Typography>
                <Typography>
                  Costo ext.:{" "}
                  <strong>{currencyGTQ.format(parseFloat(resumenGanancia?.totales_servicios?.total_costo_externo) || 0)}</strong>
                </Typography>
                <Typography>
                  Ganancia:{" "}
                  <strong>{currencyGTQ.format(parseFloat(resumenGanancia?.totales_servicios?.ganancia) || 0)}</strong>
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Boleta
                </Typography>
                <Typography>
                  Costo boleta: <strong>{currencyGTQ.format(parseFloat(resumenGanancia?.costo_boleta) || 0)}</strong>
                </Typography>
                <Typography>
                  Ganancia neta: <strong>{currencyGTQ.format(parseFloat(resumenGanancia?.ganancia_neta) || 0)}</strong>
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Box mt={2}>
            <Typography variant="h6" gutterBottom>
              Ganancia por producto
            </Typography>
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Producto</TableCell>
                      <TableCell>Cantidad</TableCell>
                      <TableCell>Venta</TableCell>
                      <TableCell>Costo</TableCell>
                      <TableCell>Ganancia</TableCell>
                    </TableRow>
                  </TableHead>
                <TableBody>
                  {(resumenGanancia?.productos || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        Sin registros
                      </TableCell>
                    </TableRow>
                  ) : (
                    (resumenGanancia?.productos || []).map((p, idx) => (
                      <TableRow key={`${p.id_producto}-${idx}`}>
                        <TableCell>{p.descripcion}</TableCell>
                        <TableCell>{p.cantidad}</TableCell>
                        <TableCell>{currencyGTQ.format(parseFloat(p.total_venta) || 0)}</TableCell>
                        <TableCell>{currencyGTQ.format(parseFloat(p.total_costo) || 0)}</TableCell>
                        <TableCell>{currencyGTQ.format(parseFloat(p.ganancia) || 0)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box mt={2}>
            <Typography variant="h6" gutterBottom>
              Servicios (agrupado)
            </Typography>
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Categoría</TableCell>
                      <TableCell>Venta</TableCell>
                      <TableCell>Costo ext.</TableCell>
                      <TableCell>Ganancia</TableCell>
                    </TableRow>
                  </TableHead>
                <TableBody>
                  {(resumenGanancia?.servicios || []).length === 0 ? (
                    <TableRow>
	                  <TableCell colSpan={mostrarGanancia ? 6 : 4} align="center">
                        Sin registros
                      </TableCell>
                    </TableRow>
                  ) : (
                    (resumenGanancia?.servicios || []).map((s, idx) => (
                      <TableRow key={`${s.categoria}-${idx}`}>
                        <TableCell>{s.categoria}</TableCell>
                        <TableCell>{currencyGTQ.format(parseFloat(s.total_venta) || 0)}</TableCell>
                        <TableCell>{currencyGTQ.format(parseFloat(s.total_costo_externo) || 0)}</TableCell>
                        <TableCell>{currencyGTQ.format(parseFloat(s.ganancia) || 0)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      )}

      {/* Listados por tipo */}
      <Box mt={4}>
        {tiposAMostrar.map((tipoItem) => {
          const rows = movimientosPorTipo(tipoItem);
          return (
            <Box key={tipoItem} mb={4}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  backgroundColor: "#eeeeee",
                  color: "#000",
                  px: 2,
                  py: 1,
                  borderRadius: 1
                }}
              >
                {TIPO_LABEL[tipoItem]}
              </Typography>

              {isXs ? (
                // ====== MÓVIL: TARJETAS ======
                <Box>
                  {rows.length === 0 ? (
                    <Paper sx={{ p: 2, borderRadius: 2 }}>
                      <Typography align="center" color="text.secondary">
                        Sin registros
                      </Typography>
                    </Paper>
                  ) : (
                    rows.map((m, idx) => (
                      <Paper key={`${tipoItem}-${m.id}-${idx}`} sx={{ p: 2, mb: 1.5, borderRadius: 2, boxShadow: 1 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Box>
                            <Typography variant="subtitle1" fontWeight={700}>
                              #{m.id} — {m.nombre_cliente || "—"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {m.fecha?.slice(0, 10) || "—"}
                            </Typography>
                          </Box>
                          <Typography variant="subtitle2">
                            {currencyGTQ.format(parseFloat(m.total) || 0)}
                          </Typography>
                        </Stack>

                        <Divider sx={{ my: 1 }} />

                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="caption" color="text.secondary">
                            {m.tipo_movimiento}
                          </Typography>

                          {["contado", "credito"].includes(tipoItem) ? (
                            <Button size="small" onClick={() => verDetalle(m.id)}>
                              👁 Ver
                            </Button>
                          ) : (
                            <Typography variant="caption" color="text.secondary">N/A</Typography>
                          )}
                        </Stack>
                      </Paper>
                    ))
                  )}
                </Box>
              ) : (
                // ====== DESKTOP: TABLA ======
                <TableContainer component={Paper} sx={{ maxHeight: 480, borderRadius: 2 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Cliente</TableCell>
                        <TableCell>Fecha</TableCell>
                        <TableCell>Total</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Acción</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            Sin registros
                          </TableCell>
                        </TableRow>
                      )}
                      {rows.map((m, idx) => (
                        <TableRow key={`${tipoItem}-${m.id}-${idx}`}>
                          <TableCell>{m.id}</TableCell>
                          <TableCell>{m.nombre_cliente}</TableCell>
                          <TableCell>{m.fecha?.slice(0, 10)}</TableCell>
                          <TableCell>
                            {currencyGTQ.format(parseFloat(m.total) || 0)}
                          </TableCell>
                          <TableCell>{m.tipo_movimiento}</TableCell>
                          <TableCell>
                            {["contado", "credito"].includes(tipoItem) ? (
                              <Button size="small" onClick={() => verDetalle(m.id)}>Ver</Button>
                            ) : (
                              "N/A"
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          );
        })}

        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="h6" align="right">
            Total General del Día (sin crédito):{" "}
            <strong>{currencyGTQ.format(totalSinCredito)}</strong>
          </Typography>
        </Paper>
      </Box>

      {/* Modal Detalle */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>Detalle de Venta #{ventaSeleccionada}</DialogTitle>
        <DialogContent dividers>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Producto</TableCell>
                <TableCell>Cantidad</TableCell>
                <TableCell>Precio Unitario</TableCell>
                <TableCell>Total</TableCell>
                {mostrarGanancia && <TableCell>Costo</TableCell>}
                {mostrarGanancia && <TableCell>Ganancia</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {detalle.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Sin ítems
                  </TableCell>
                </TableRow>
              )}
              {detalle.map((d, i) => (
                <TableRow key={i}>
                  <TableCell>{d.descripcion}</TableCell>
                  <TableCell>{d.cantidad}</TableCell>
	                  <TableCell>{currencyGTQ.format(parseFloat(d.precio_unitario) || 0)}</TableCell>
	                  <TableCell>{currencyGTQ.format(parseFloat(d.total) || 0)}</TableCell>
	                  {mostrarGanancia && (
	                    <TableCell>{currencyGTQ.format(parseFloat(d.total_costo) || 0)}</TableCell>
	                  )}
	                  {mostrarGanancia && (
	                    <TableCell>{currencyGTQ.format(parseFloat(d.ganancia) || 0)}</TableCell>
	                  )}
	                </TableRow>
              ))}
            </TableBody>
	          </Table>

	          {mostrarGanancia && detalle.length > 0 && (
	            <Box mt={2}>
	              <Divider />
	              <Typography variant="subtitle1" align="right" sx={{ mt: 1 }}>
	                Ganancia de la venta:{" "}
	                <strong>
	                  {currencyGTQ.format(
	                    detalle.reduce((acc, d) => acc + (parseFloat(d.ganancia) || 0), 0)
	                  )}
	                </strong>
	              </Typography>
	            </Box>
	          )}
	        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ReportePorFecha;
